'use client'

import { useQueryClient } from '@tanstack/react-query'
import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth/hooks'
import { usePapertradeOnboard } from '@/hooks/use-papertrade-onboard'
import { isUserRejection, PREPARE_WINDOW_MS } from '@/lib/papertrade/onboarding'
import {
  isCurrentAttempt,
  phaseAfterConfirmError,
  phaseAfterDoneError,
  type SignPhase,
} from '@/lib/papertrade/sign-phase'
import { beginManualWalletAction } from '@/lib/wallet/connect'
import { accountKeys } from '@/service/account.hook'

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message
  const m = (e as { message?: unknown } | null)?.message
  return typeof m === 'string' && m ? m : fallback
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const mm = Math.floor(total / 60)
  const ss = String(total % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

type Props = {
  accountId: string
  /** Connected wallet address; must already be connected. */
  wallet: string
  /** Called once the key is registered. If it rejects, the message is shown with a retry. */
  onDone: () => void | Promise<void>
}

/**
 * Papertrade wallet link: sign the session-key typed data, then register it.
 * Used by both the connection wizard and settings re-sign. All state belongs to
 * one (accountId, wallet) pair and is dropped when either changes or on unmount.
 */
export function PapertradeSignStep({ accountId, wallet, onDone }: Props) {
  const t = useTranslations('accountConnection')
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { prepareAndSign, confirm } = usePapertradeOnboard()
  const [phase, setPhase] = useState<SignPhase>({ kind: 'idle' })
  const [now, setNow] = useState(() => Date.now())
  // Identity of the attempt on screen; null once unmounted. Every await boundary
  // compares against it, so a late result never reaches onDone or setState.
  const attemptKey = `${accountId}:${wallet}`
  const currentKey = useRef<string | null>(attemptKey)
  useEffect(() => {
    currentKey.current = attemptKey
    setPhase({ kind: 'idle' })
    return () => {
      currentKey.current = null
    }
  }, [attemptKey])

  const preparedAt = phase.kind === 'confirming' || phase.kind === 'retry' ? phase.preparedAt : null

  // Countdown while a signature is held; at 0 the backend has dropped the prepare.
  useEffect(() => {
    if (preparedAt === null) return
    const tick = () => {
      const t0 = Date.now()
      setNow(t0)
      if (t0 - preparedAt >= PREPARE_WINDOW_MS) {
        setPhase((p) =>
          p.kind === 'retry' ? { kind: 'restart', message: t('papertradeWindowExpired') } : p
        )
      }
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [preparedAt, t])

  // Ref guards the synchronous double-click; state disables the button.
  const finishing = useRef(false)
  const [isFinishing, setIsFinishing] = useState(false)

  const finish = useCallback(
    async (key: string) => {
      if (finishing.current) return
      finishing.current = true
      setIsFinishing(true)
      try {
        await onDone()
      } catch (e) {
        if (!isCurrentAttempt(key, currentKey.current)) return
        setPhase(phaseAfterDoneError(errorMessage(e, t('papertradeRegisterFailed'))))
      } finally {
        finishing.current = false
        setIsFinishing(false)
      }
    },
    [onDone, t]
  )

  const runConfirm = useCallback(
    async (signature: string, signedAt: number) => {
      const key = attemptKey
      setPhase({ kind: 'confirming', signature, preparedAt: signedAt })
      try {
        await confirm(accountId, wallet, signature)
      } catch (e) {
        if (!isCurrentAttempt(key, currentKey.current)) return
        setPhase(
          phaseAfterConfirmError(
            errorMessage(e, t('papertradeRegisterFailed')),
            signature,
            signedAt,
            e
          )
        )
        return
      }
      if (!isCurrentAttempt(key, currentKey.current)) return
      await queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      if (!isCurrentAttempt(key, currentKey.current)) return
      await finish(key)
    },
    [attemptKey, accountId, wallet, confirm, queryClient, user?.userId, finish, t]
  )

  const runSign = useCallback(async () => {
    const key = attemptKey
    setPhase({ kind: 'signing' })
    let signed: { signature: string; preparedAt: number }
    try {
      signed = await prepareAndSign(accountId, wallet)
    } catch (e) {
      if (!isCurrentAttempt(key, currentKey.current)) return
      if (isUserRejection(e)) {
        setPhase({ kind: 'idle' })
      } else {
        setPhase({ kind: 'restart', message: errorMessage(e, t('papertradeSignFailed')) })
      }
      return
    }
    if (!isCurrentAttempt(key, currentKey.current)) return
    await runConfirm(signed.signature, signed.preparedAt)
  }, [attemptKey, accountId, wallet, prepareAndSign, runConfirm, t])

  const handleSignClick = () => {
    try {
      // Refresh synchronously while the click still grants user activation.
      beginManualWalletAction()
    } catch (e) {
      setPhase({ kind: 'restart', message: errorMessage(e, t('papertradeSignFailed')) })
      return
    }
    void runSign()
  }

  // The user just chose to link (or re-sign), so open the wallet prompt without
  // a second click. Once per pair: a rejection lands on idle and waits for the
  // button. The ref survives StrictMode's effect re-run, so it signs once.
  const autoStarted = useRef<string | null>(null)
  useEffect(() => {
    if (autoStarted.current === attemptKey) return
    autoStarted.current = attemptKey
    void runSign()
  }, [attemptKey, runSign])

  const remaining = preparedAt === null ? null : PREPARE_WINDOW_MS - (now - preparedAt)

  return (
    <div className="flex flex-col gap-3 text-left">
      <div className="flex flex-col gap-0.5">
        <span className="text-foreground/60 text-xs">{t('papertradeWallet')}</span>
        <code className="text-foreground text-xs break-all">{wallet}</code>
      </div>

      {(phase.kind === 'idle' || phase.kind === 'signing') && (
        <Button
          type="button"
          variant="cta"
          className="mx-auto w-fit"
          disabled={phase.kind === 'signing'}
          onClick={handleSignClick}
        >
          {phase.kind === 'signing' ? t('papertradeSigning') : t('papertradeSignButton')}
        </Button>
      )}

      {phase.kind === 'confirming' && (
        <p className="text-foreground/70 flex items-center gap-2 text-sm">
          <Loader2Icon className="size-4 animate-spin" aria-hidden="true" />
          {t('papertradeRegistering')}
        </p>
      )}

      {(phase.kind === 'retry' ||
        phase.kind === 'restart' ||
        phase.kind === 'fatal' ||
        phase.kind === 'finish-failed') && (
        <p role="alert" className="text-chart-red text-sm">
          {phase.message}
        </p>
      )}

      {phase.kind === 'retry' && (
        <Button
          type="button"
          variant="cta"
          className="mx-auto w-fit"
          onClick={() => void runConfirm(phase.signature, phase.preparedAt)}
        >
          {t('papertradeRetry')}
        </Button>
      )}

      {phase.kind === 'finish-failed' && (
        <Button
          type="button"
          variant="cta"
          className="mx-auto w-fit"
          disabled={isFinishing}
          onClick={() => void finish(attemptKey)}
        >
          {t('papertradeRetry')}
        </Button>
      )}

      {phase.kind === 'restart' && (
        <Button type="button" variant="cta" className="mx-auto w-fit" onClick={handleSignClick}>
          {t('papertradeSignAgain')}
        </Button>
      )}

      {remaining !== null && remaining > 0 && (
        <p className="text-foreground/50 text-xs">
          {t('papertradeWindowCountdown', { time: formatRemaining(remaining) })}
        </p>
      )}
    </div>
  )
}
